import { useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import type { DraftSection, GoLiveData } from '../types';
import { sectionList, sectionText } from '../utils/draftSectionText';
import { goLiveSteps } from '../utils/reviewsReport';
import { StepTitle } from './BettyReviewsReport';
import styles from './TaskPanels.module.css';

/** Where a step's own words send the owner, as a link: "Settings → Team & Staff" opens Team & Staff. */
const SETTINGS_LINKS: Array<[RegExp, string, string]> = [
  [/settings\s*(?:→|->|>)\s*team/i, '/settings/team', 'Open Settings → Team & Staff'],
  [
    /settings\s*(?:→|->|>)\s*(?:communication|texting)/i,
    '/settings/communication',
    'Open Settings → Communication',
  ],
  [
    /settings\s*(?:→|->|>)\s*(?:practice|locations)/i,
    '/settings/practice',
    'Open Settings → Practice & Locations',
  ],
];

/**
 * A front desk routine or a go-live step: what Betty saw (her take), the go-live tracker for a
 * setup step, the change or step with who does it, the staff note with Copy, the setting that
 * changes on approval, a checklist of what to confirm, and why it matters folded away.
 */
export function BettyProposalPanel({
  skillKey,
  sections,
  goLive,
  agentName,
}: {
  skillKey: string;
  sections: DraftSection[];
  goLive?: GoLiveData;
  agentName: string;
}) {
  const isSetup = skillKey === 'review_program_setup';
  const seen = sectionText(sections, isSetup ? 'blocker' : 'problem');
  const body = sectionText(sections, isSetup ? 'step' : 'change');
  const owner = sectionText(sections, 'owner');
  const staffNote = sectionText(sections, 'staffNote');
  const setting = sectionText(sections, 'settingsChange');
  const checks = sectionList(sections, 'checks');
  const why = sectionText(sections, 'why');
  const link = SETTINGS_LINKS.find(([re]) => re.test(body));
  const tracker = isSetup && goLive ? goLiveSteps(goLive) : null;
  const [copied, setCopied] = useState(false);
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const [whyOpen, setWhyOpen] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(staffNote);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  let step = 0;
  const nextStep = () => (step += 1);

  return (
    <div className={styles.stack}>
      <section className={styles.take} aria-label={`${agentName}'s take`}>
        <div className={styles.takeHead}>
          <div className={styles.takeKicker}>
            {tracker ? 'Getting your review texts live' : `What ${agentName} saw`}
          </div>
          {tracker && goLive?.testMode !== null && goLive?.testMode !== undefined && (
            <span className={styles.modePill}>Test mode: {goLive.testMode ? 'on' : 'off'}</span>
          )}
        </div>
        {seen && <p className={styles.takeText}>{seen}</p>}
        {tracker && (
          <div>
            <div className={styles.progressLabel}>
              {tracker.done} of {tracker.total} steps done
            </div>
            <progress
              className={styles.progress}
              max={tracker.total}
              value={tracker.done}
              aria-label="Go-live steps done"
            />
          </div>
        )}
      </section>

      <div className={tracker ? styles.withTracker : undefined}>
        {tracker && (
          <ol className={styles.tracker} aria-label="Go-live steps">
            {tracker.steps.map((s, i) => (
              <li
                key={s.key}
                className={clsx(
                  styles.trackerRow,
                  s.state === 'current' && styles.trackerRowCurrent,
                )}
                aria-current={s.state === 'current' ? 'step' : undefined}
              >
                <span
                  className={clsx(
                    styles.trackerDot,
                    s.state === 'done' && styles.trackerDotDone,
                    s.state === 'current' && styles.trackerDotCurrent,
                  )}
                  aria-hidden="true"
                >
                  {s.state === 'done' ? <Check size={13} /> : i + 1}
                </span>
                <span>
                  <span className={styles.trackerLabel}>{s.label}</span>
                  <span className={styles.trackerState}>
                    {s.state === 'done'
                      ? 'Done'
                      : s.state === 'current'
                        ? 'This step'
                        : s.state === 'unknown'
                          ? 'Not checked yet'
                          : 'Not yet'}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        <div className={styles.stack}>
          {body && (
            <section className={styles.section}>
              <StepTitle step={nextStep()} title={isSetup ? 'What to do' : 'The change'} />
              <div className={styles.card}>
                <div className={styles.markdown}>
                  <ReactMarkdown>{body}</ReactMarkdown>
                </div>
                {owner && (
                  <div className={styles.chips}>
                    <span className={styles.chip}>
                      <strong>Who:</strong> {owner}
                    </span>
                  </div>
                )}
                {link && (
                  <div>
                    <Link className={styles.secondaryLink} to={link[1]}>
                      {link[2]}
                    </Link>
                  </div>
                )}
              </div>
            </section>
          )}

          {staffNote && (
            <section className={styles.section}>
              <StepTitle
                step={nextStep()}
                title="A note you can send your front desk"
                sub={`Ready to forward as is. ${agentName} doesn't send it.`}
              />
              <div className={styles.noteCard}>
                <p className={styles.noteText}>{staffNote}</p>
                <div>
                  <Button variant="secondary" size="sm" onClick={copy}>
                    {copied ? 'Copied' : 'Copy note'}
                  </Button>
                </div>
              </div>
            </section>
          )}

          {setting && (
            <section className={styles.section}>
              <StepTitle step={nextStep()} title="Setting that changes when you approve" />
              <div className={styles.card}>
                <div className={styles.settingLine}>{setting}</div>
              </div>
            </section>
          )}

          {checks.length > 0 && (
            <section className={styles.section}>
              <StepTitle
                step={nextStep()}
                title={isSetup ? 'Before you mark it done' : 'Before you approve'}
                sub={`${Object.values(ticked).filter(Boolean).length} of ${checks.length} checked. They're for you; ${agentName} doesn't see them.`}
              />
              <div className={styles.card}>
                {checks.map((item, i) => (
                  <label key={i} className={styles.checkItem}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={!!ticked[i]}
                      onChange={() => setTicked((t) => ({ ...t, [i]: !t[i] }))}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {why && (
        <section className={styles.fold}>
          <button
            type="button"
            className={styles.foldHeader}
            aria-expanded={whyOpen}
            onClick={() => setWhyOpen((v) => !v)}
          >
            <span className={styles.foldTitle}>
              {isSetup ? 'Why this step now' : 'Why this matters'}{' '}
              <span className={styles.optional}>Optional</span>
            </span>
            <ChevronDown
              size={18}
              aria-hidden="true"
              className={clsx(styles.chevron, whyOpen && styles.chevronOpen)}
            />
          </button>
          {whyOpen && (
            <div className={clsx(styles.foldBody, styles.markdown)}>
              <ReactMarkdown>{why}</ReactMarkdown>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
